import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  History, RefreshCw, Search, User,
  LogIn, LogOut, Plus, Pencil, Trash2, FileText, 
  ShoppingCart, CreditCard, Receipt, Package, Users, Building2,
  ExternalLink
} from 'lucide-react';

const HistorialActividadPage = () => {
  const navigate = useNavigate();
  const { user } = useApp();
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filtros
  const [filtroModulo, setFiltroModulo] = useState('all');
  const [filtroAccion, setFiltroAccion] = useState('all');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [limit, setLimit] = useState(100);

  const cargarHistorial = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (filtroModulo && filtroModulo !== 'all') params.modulo = filtroModulo;
      if (filtroAccion && filtroAccion !== 'all') params.accion = filtroAccion;
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      
      const res = await api.get('/historial', { params });
      setHistorial(res.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  }, [filtroModulo, filtroAccion, fechaDesde, fechaHasta, limit]);

  useEffect(() => {
    cargarHistorial();
  }, [cargarHistorial]);

  // Obtener la ruta de navegación según el módulo, entidad y acción
  const getNavigationPath = (h) => {
    const { modulo, entidad_tipo, entidad_id, accion, detalles } = h;
    
    // Si es AUTH, no hay navegación
    if (modulo === 'AUTH') return null;
    
    // Si fue eliminado, ir a la vista listado del módulo
    if (accion === 'ELIMINAR') {
      const listRoutes = {
        'COMPRAS': detalles?.tipo_compra === 'GASTO' ? '/gastos' : '/facturas-proveedor',
        'ORDENES_COMPRA': '/ordenes-compra',
        'PAGOS': '/pagos',
        'VENTAS': '/ventas',
        'NOMINA': '/planillas',
        'USUARIOS': '/usuarios'
      };
      return listRoutes[modulo] || null;
    }
    
    // Si fue creado o editado, ir al detalle/edición
    if (accion === 'CREAR' || accion === 'EDITAR') {
      if (entidad_tipo && entidad_id) {
        const editRoutes = {
          'compra': detalles?.tipo_compra === 'GASTO' ? `/gastos/${entidad_id}` : `/facturas-proveedor/${entidad_id}`,
          'orden_compra': `/ordenes-compra/${entidad_id}`,
          'pago': `/pagos`, // Pagos no tiene vista de edición individual, ir al listado
          'venta': `/ventas/${entidad_id}`,
          'usuario': `/usuarios`
        };
        return editRoutes[entidad_tipo] || null;
      }
    }
    
    return null;
  };

  // Manejar click en una actividad
  const handleActivityClick = (h) => {
    const path = getNavigationPath(h);
    if (path) {
      navigate(path);
    }
  };

  // Configuración visual por acción
  const getAccionConfig = (accion) => {
    const configs = {
      'CREAR': { 
        icon: Plus, 
        bg: 'bg-green-500', 
        text: 'text-green-600',
        bgLight: 'bg-green-50',
        border: 'border-green-200',
        label: 'Creó'
      },
      'EDITAR': { 
        icon: Pencil, 
        bg: 'bg-amber-500', 
        text: 'text-amber-600',
        bgLight: 'bg-amber-50',
        border: 'border-amber-200',
        label: 'Editó'
      },
      'ELIMINAR': { 
        icon: Trash2, 
        bg: 'bg-red-500', 
        text: 'text-red-600',
        bgLight: 'bg-red-50',
        border: 'border-red-200',
        label: 'Eliminó'
      },
      'LOGIN': { 
        icon: LogIn, 
        bg: 'bg-blue-500', 
        text: 'text-blue-600',
        bgLight: 'bg-blue-50',
        border: 'border-blue-200',
        label: 'Inició sesión'
      },
      'LOGOUT': { 
        icon: LogOut, 
        bg: 'bg-gray-500', 
        text: 'text-gray-600',
        bgLight: 'bg-gray-50',
        border: 'border-gray-200',
        label: 'Cerró sesión'
      },
      'LOGIN_FALLIDO': { 
        icon: LogIn, 
        bg: 'bg-red-500', 
        text: 'text-red-600',
        bgLight: 'bg-red-50',
        border: 'border-red-200',
        label: 'Intento fallido'
      }
    };
    return configs[accion] || { 
      icon: FileText, 
      bg: 'bg-gray-500', 
      text: 'text-gray-600',
      bgLight: 'bg-gray-50',
      border: 'border-gray-200',
      label: accion 
    };
  };

  // Icono y nombre del módulo
  const getModuloConfig = (modulo) => {
    const configs = {
      'COMPRAS': { icon: Receipt, name: 'Factura/Gasto', color: 'text-green-600' },
      'ORDENES_COMPRA': { icon: ShoppingCart, name: 'Orden de Compra', color: 'text-cyan-600' },
      'PAGOS': { icon: CreditCard, name: 'Pago', color: 'text-teal-600' },
      'VENTAS': { icon: FileText, name: 'Venta', color: 'text-amber-600' },
      'AUTH': { icon: User, name: '', color: 'text-purple-600' },
      'USUARIOS': { icon: Users, name: 'Usuario', color: 'text-blue-600' },
      'NOMINA': { icon: Users, name: 'Nómina', color: 'text-pink-600' },
      'CATALOGOS': { icon: Package, name: 'Catálogo', color: 'text-indigo-600' }
    };
    return configs[modulo] || { icon: FileText, name: modulo, color: 'text-gray-600' };
  };

  // Formatear detalles bonito
  const formatDetalleValue = (key, value) => {
    if (key === 'total_me' || key === 'total_pen' || key === 'monto_me' || key === 'monto_pen' || key === 'total') {
      return `S/ ${parseFloat(value).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`;
    }
    if (key === 'tipo_compra') {
      return value === 'FACTURA_PROVEEDOR' ? 'Factura' : value === 'GASTO' ? 'Gasto' : value;
    }
    if (key === 'tipo') {
      return value === 'EGRESO' ? 'Egreso' : value === 'INGRESO' ? 'Ingreso' : value;
    }
    if (typeof value === 'boolean') {
      return value ? 'Sí' : 'No';
    }
    return value;
  };

  const formatKeyName = (key) => {
    const names = {
      'tipo_compra': 'Tipo',
      'nro_doc_full': 'Número',
      'total_me': 'Total',
      'total_pen': 'Total PEN',
      'monto_me': 'Monto',
      'monto_pen': 'Monto PEN',
      'proveedor': 'Proveedor',
      'proveedor_id': 'Proveedor ID',
      'estado': 'Estado',
      'lineas': 'Líneas',
      'articulos': 'Artículos',
      'aplicaciones': 'Aplicaciones',
      'medio_pago': 'Medio de Pago',
      'ref_pago': 'Referencia',
      'numero': 'Número',
      'igv_incluido': 'IGV Incluido',
      'total': 'Total'
    };
    return names[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Agrupar por fecha
  const agruparPorFecha = (items) => {
    const grupos = {};
    items.forEach(item => {
      const fecha = item.created_at ? new Date(item.created_at).toLocaleDateString('es-PE', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }) : 'Sin fecha';
      if (!grupos[fecha]) grupos[fecha] = [];
      grupos[fecha].push(item);
    });
    return grupos;
  };

  const historialAgrupado = agruparPorFecha(historial);
  const modulos = [...new Set(historial.map(h => h.modulo))].sort();
  const acciones = [...new Set(historial.map(h => h.accion))].sort();

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="historial-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <History className="w-7 h-7 text-indigo-600" />
            Historial de Actividad
          </h1>
          <p className="text-gray-500 text-sm">
            Registro de cambios en el sistema
          </p>
        </div>
        <Button variant="outline" onClick={cargarHistorial} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-40">
              <Label className="text-xs text-gray-500">Módulo</Label>
              <Select value={filtroModulo} onValueChange={setFiltroModulo}>
                <SelectTrigger className="mt-1 h-9">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {modulos.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs text-gray-500">Acción</Label>
              <Select value={filtroAccion} onValueChange={setFiltroAccion}>
                <SelectTrigger className="mt-1 h-9">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {acciones.map(a => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Desde</Label>
              <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Hasta</Label>
              <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="mt-1 h-9" />
            </div>
            <Button onClick={cargarHistorial} disabled={loading} size="sm">
              <Search className="w-4 h-4 mr-1" />
              Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Timeline de actividades */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : historial.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-400">
            <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No hay actividades registradas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(historialAgrupado).map(([fecha, items]) => (
            <div key={fecha}>
              {/* Fecha como separador */}
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1 bg-gray-200"></div>
                <span className="text-sm font-medium text-gray-500 capitalize">{fecha}</span>
                <div className="h-px flex-1 bg-gray-200"></div>
              </div>

              {/* Cards de actividades */}
              <div className="space-y-3">
                {items.map((h) => {
                  const accionConfig = getAccionConfig(h.accion);
                  const moduloConfig = getModuloConfig(h.modulo);
                  const IconAccion = accionConfig.icon;
                  const IconModulo = moduloConfig.icon;
                  const isAuthAction = h.modulo === 'AUTH';
                  const navigationPath = getNavigationPath(h);
                  const isClickable = !!navigationPath;

                  return (
                    <Card 
                      key={h.id} 
                      className={`overflow-hidden border-l-4 ${accionConfig.border} transition-all ${
                        isClickable ? 'hover:shadow-lg hover:scale-[1.01] cursor-pointer' : ''
                      }`}
                      onClick={() => isClickable && handleActivityClick(h)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          {/* Icono de acción */}
                          <div className={`p-2 rounded-full ${accionConfig.bg} text-white flex-shrink-0`}>
                            <IconAccion className="w-5 h-5" />
                          </div>

                          {/* Contenido principal */}
                          <div className="flex-1 min-w-0">
                            {/* Línea principal */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-gray-900">
                                {h.usuario_nombre || 'Sistema'}
                              </span>
                              <span className={`font-medium ${accionConfig.text}`}>
                                {accionConfig.label}
                              </span>
                              {!isAuthAction && (
                                <>
                                  <IconModulo className={`w-4 h-4 ${moduloConfig.color}`} />
                                  <span className="text-gray-700">
                                    {moduloConfig.name}
                                  </span>
                                </>
                              )}
                              {isClickable && (
                                <ExternalLink className="w-3.5 h-3.5 text-gray-400 ml-1" />
                              )}
                            </div>

                            {/* Descripción */}
                            {!isAuthAction && h.descripcion && (
                              <p className="text-gray-600 mt-1 text-sm">
                                {h.descripcion}
                              </p>
                            )}

                            {/* Detalles en pills */}
                            {h.detalles && !isAuthAction && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                {Object.entries(h.detalles)
                                  .filter(([key]) => !['proveedor_id', 'empresa_id'].includes(key))
                                  .map(([key, value]) => (
                                    <Badge 
                                      key={key} 
                                      variant="secondary" 
                                      className="bg-gray-100 text-gray-700 font-normal"
                                    >
                                      <span className="text-gray-500 mr-1">{formatKeyName(key)}:</span>
                                      <span className="font-medium">{formatDetalleValue(key, value)}</span>
                                    </Badge>
                                  ))}
                              </div>
                            )}

                            {/* Entidad y enlace */}
                            <div className="flex items-center gap-2 mt-2">
                              {h.entidad_tipo && h.entidad_id && (
                                <Badge variant="outline" className="text-xs">
                                  {h.entidad_tipo} #{h.entidad_id}
                                </Badge>
                              )}
                              {isClickable && (
                                <span className="text-xs text-indigo-500 hover:text-indigo-700">
                                  {h.accion === 'ELIMINAR' ? 'Ver listado →' : 'Ver registro →'}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Hora */}
                          <div className="text-right flex-shrink-0">
                            <span className="text-xs text-gray-400">
                              {h.created_at ? new Date(h.created_at).toLocaleTimeString('es-PE', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              }) : ''}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistorialActividadPage;
